import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5126';

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS = ['Lu','Ma','Me','Je','Ve','Sa','Di'];
const SERVICES = ['Plomberie','Chauffage','Climatisation','Salle de bain clé en main','Pompe à chaleur','Autre'];

const todayMidnight = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const toISODate = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
const formatDate = (date) => date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/* ── Modal content ── */
const BookingModal = ({ onClose }) => {
  const today = todayMidnight();
  const [viewDate, setViewDate]       = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [availableDates, setAvailableDates] = useState([]);
  const [calLoading, setCalLoading]   = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm]               = useState({ prenom:'', nom:'', telephone:'', email:'', service:'' });
  const [loading, setLoading]         = useState(false);
  const [success, setSuccess]         = useState(false);
  const [error, setError]             = useState('');

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const fetchCalendar = useCallback(async (y, m) => {
    setCalLoading(true);
    setAvailableDates([]);
    try {
      const { data } = await axios.get(`${API}/availability/calendar`, { params: { year: y, month: m + 1 } });
      setAvailableDates(data);
    } finally {
      setCalLoading(false);
    }
  }, []);

  useEffect(() => { fetchCalendar(year, month); }, [fetchCalendar, year, month]);

  /* lock body scroll while modal open */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const fetchSlots = useCallback(async (date) => {
    setSlotsLoading(true);
    setAvailableSlots([]);
    setSelectedTime('');
    try {
      const { data } = await axios.get(`${API}/availability/slots`, { params: { date: toISODate(date) } });
      setAvailableSlots(data);
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const handleDayClick = (date) => {
    if (!date || !availableDates.includes(toISODate(date))) return;
    setSelectedDate(date);
    fetchSlots(date);
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rawFirst    = new Date(year, month, 1).getDay();
  const firstOffset = rawFirst === 0 ? 6 : rawFirst - 1;
  const days = [];
  for (let i = 0; i < firstOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

  const isAvailable = (date) => date && availableDates.includes(toISODate(date));
  const isSelected  = (date) => date && selectedDate && date.toDateString() === selectedDate.toDateString();
  const isToday     = (date) => date && date.toDateString() === today.toDateString();
  const canPrev     = () => !(year === today.getFullYear() && month === today.getMonth());

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handlePrev = () => { setViewDate(new Date(year, month - 1, 1)); setSelectedDate(null); setSelectedTime(''); };
  const handleNext = () => { setViewDate(new Date(year, month + 1, 1)); setSelectedDate(null); setSelectedTime(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) { setError('Veuillez sélectionner une date et un créneau.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/rdv`, { ...form, date: toISODate(selectedDate), heure: selectedTime });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Une erreur est survenue, veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="booking-overlay" onClick={onClose}>
      <div className="booking-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="booking-modal-header">
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 2 }}>Appel gratuit</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--color-text)' }}>Planifier un appel</div>
          </div>
          <button className="booking-modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        {/* Body */}
        <div className="booking-modal-body">
          {success ? (
            <div className="rdv-success" style={{ padding: '40px 0' }}>
              <div className="rdv-success-icon">✓</div>
              <h3>Appel planifié !</h3>
              <p>Votre appel du <strong>{formatDate(selectedDate)}</strong> à <strong>{selectedTime}</strong> a bien été enregistré.<br/>Nous vous confirmons sous 24 h.</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <button className="btn btn-primary" onClick={() => { setSuccess(false); setSelectedDate(null); setSelectedTime(''); setForm({ prenom:'', nom:'', telephone:'', email:'', service:'' }); }}>
                  Planifier un autre appel
                </button>
                <button className="btn btn-outline" onClick={onClose}>Fermer</button>
              </div>
            </div>
          ) : (
            <div className="rdv-layout">

              {/* Calendrier */}
              <div className="rdv-left">
                <div className="rdv-cal">
                  <div className="rdv-cal-header">
                    <button className="rdv-cal-nav" type="button" onClick={handlePrev} disabled={!canPrev()}>‹</button>
                    <span className="rdv-cal-title">{MONTHS[month]} {year}</span>
                    <button className="rdv-cal-nav" type="button" onClick={handleNext}>›</button>
                  </div>
                  <div className="rdv-cal-grid">
                    {DAYS.map(d => <div key={d} className="rdv-cal-dayname">{d}</div>)}
                    {calLoading
                      ? Array.from({ length: 35 }).map((_, i) => <div key={i} className="rdv-cal-day disabled" />)
                      : days.map((date, i) => {
                          const available = isAvailable(date);
                          const cls = ['rdv-cal-day', !date && 'empty', date && !available && 'disabled', isSelected(date) && 'selected', isToday(date) && 'today'].filter(Boolean).join(' ');
                          return (
                            <button key={i} type="button" className={cls} onClick={() => handleDayClick(date)} disabled={!date || !available}>
                              {date?.getDate()}
                            </button>
                          );
                        })
                    }
                  </div>
                  {!calLoading && availableDates.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.85rem', padding: '12px 0' }}>
                      Aucun créneau disponible ce mois-ci.
                    </div>
                  )}
                </div>

                {selectedDate && (
                  <div className="rdv-slots">
                    <div className="rdv-slots-label">
                      {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                    {slotsLoading ? (
                      <div style={{ color: '#6b7280', fontSize: '0.85rem', padding: '8px 0' }}>Chargement...</div>
                    ) : availableSlots.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '0.85rem', padding: '8px 0' }}>Plus aucun créneau disponible.</div>
                    ) : (
                      <div className="rdv-slots-grid">
                        {availableSlots.map(t => (
                          <button key={t} type="button" className={`rdv-slot${selectedTime === t ? ' selected' : ''}`} onClick={() => setSelectedTime(t)}>{t}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Formulaire */}
              <div className="rdv-right">
                <form onSubmit={handleSubmit} className="rdv-form">
                  <div className="rdv-form-title">Vos coordonnées</div>
                  {error && <div className="form-alert error">{error}</div>}

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Prénom *</label>
                      <input className="form-input" value={form.prenom} onChange={set('prenom')} required placeholder="Jean" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nom *</label>
                      <input className="form-input" value={form.nom} onChange={set('nom')} required placeholder="Dupont" />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Téléphone *</label>
                      <input className="form-input" type="tel" value={form.telephone} onChange={set('telephone')} required placeholder="06 XX XX XX XX" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input className="form-input" type="email" value={form.email} onChange={set('email')} required placeholder="jean@exemple.fr" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Objet de l'appel *</label>
                    <select className="form-input" value={form.service} onChange={set('service')} required>
                      <option value="">Sélectionner un service...</option>
                      {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className={`rdv-summary${selectedDate && selectedTime ? ' filled' : ''}`}>
                    {selectedDate && selectedTime
                      ? <><span>📞</span><span>{formatDate(selectedDate)} à {selectedTime}</span></>
                      : <span>← Sélectionnez une date et un créneau</span>
                    }
                  </div>

                  <button type="submit" className="btn btn-primary btn-full" disabled={loading || !selectedDate || !selectedTime}>
                    {loading ? 'Envoi en cours...' : 'Confirmer l\'appel'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Section CTA ── */
const Booking = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <section className="section section-booking" id="rdv">
        <div className="section-inner">

          <div className="booking-section-header">
            <div className="section-tag booking-tag">Prise de rendez-vous</div>
            <h2 className="booking-section-title">Planifiez un appel gratuit</h2>
            <p className="booking-section-sub">
              Décrivez votre projet en quelques clics. Nous vous rappelons pour
              étudier votre besoin et vous proposer un devis personnalisé.
            </p>
          </div>

          <div className="booking-features">
            <div className="booking-feature">
              <span className="booking-feature-icon">📅</span>
              <strong>Lundi – Vendredi</strong>
              <span>Disponible de 9h à 18h</span>
            </div>
            <div className="booking-feature">
              <span className="booking-feature-icon">⚡</span>
              <strong>Réponse rapide</strong>
              <span>Rappel sous 24h</span>
            </div>
            <div className="booking-feature">
              <span className="booking-feature-icon">🎯</span>
              <strong>Devis personnalisé</strong>
              <span>Adapté à votre projet</span>
            </div>
          </div>

          <div className="booking-cta-card">
            <div className="booking-cta-icon">📞</div>
            <div className="booking-cta-content">
              <h3 className="booking-cta-title">Prêt à démarrer votre projet ?</h3>
              <p className="booking-cta-sub">Choisissez un créneau qui vous convient — c'est gratuit et sans engagement.</p>
            </div>
            <button className="btn btn-accent booking-cta-btn" onClick={() => setOpen(true)}>
              Choisir un créneau →
            </button>
          </div>

        </div>
      </section>

      {open && <BookingModal onClose={() => setOpen(false)} />}
    </>
  );
};

export default Booking;
